import { motion } from "framer-motion";
import {
  Activity,
  Bot,
  ChartNoAxesColumnIncreasing,
  CalendarCheck,
  CheckCircle2,
  FileText,
  Inbox,
  ListChecks,
  Mail,
  MessageSquare,
  MousePointer2,
  Phone,
  Settings,
  Send,
  Star,
  Puzzle,
  Users,
} from "lucide-react";
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Lang, t } from "../i18n";
import logoMarkSrc from "../assets/clawease-logo-mark-vector.svg";
import logoSrc from "../assets/logo_cutout_transparent.png";

type VisualType = "learn" | "catch" | "understand" | "execute" | "report";

const LEARN_CYCLE_SECONDS = 9.5;
const EXECUTE_CYCLE_SECONDS = 7.6;
const LEARN_URL = "https://booking.example.com";
const LEARN_BROWSER_TOOLBAR_HEIGHT = 60;
const LEARN_PLUGIN_LOGO_TOP = 136;
const LEARN_PLUGIN_DOCK_TOP = 0;
const LEARN_PLUGIN_DOCK_SCALE = 0.54;
const LEARN_EXPLORE_BUTTON_TOP = 210;
const LEARN_CONFIRM_BUTTON_TOP = 210;
const LEARN_BUTTON_CENTER_OFFSET = 18;
const LEARN_PLUGIN_CURSOR_MOVE_START = 3.2;
const LEARN_PLUGIN_CURSOR_LAND = 3.6;
const LEARN_PLUGIN_HOVER_START = 3.6;
const LEARN_PLUGIN_PRESS_START = 3.7;
const LEARN_PLUGIN_PRESS_END = 3.84;
const LEARN_PLUGIN_PANEL_START = 3.84;
const LEARN_PLUGIN_PANEL_END = 4.24;
const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const lerp = (from: number, to: number, progress: number) => from + (to - from) * progress;
const easeInOutQuad = (progress: number) =>
  progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
const progressBetween = (phase: number, start: number, end: number, eased = true) => {
  const progress = clamp01((phase - start) / (end - start));
  return eased ? easeInOutQuad(progress) : progress;
};
const fadeInOut = (phase: number, inStart: number, inEnd: number, outStart: number, outEnd: number) => {
  if (phase < inStart || phase >= outEnd) return 0;
  if (phase < inEnd) return progressBetween(phase, inStart, inEnd);
  if (phase < outStart) return 1;
  return 1 - progressBetween(phase, outStart, outEnd);
};

function useLoopPhase(duration: number) {
  const startRef = useRef<number | null>(null);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    let frame = 0;

    const tick = (now: number) => {
      startRef.current ??= now;
      setPhase(((now - startRef.current) / 1000) % duration);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [duration]);

  return phase;
}

const steps: Array<{
  key: string;
  visual: VisualType;
  reverse?: boolean;
  tags: string[];
}> = [
  {
    key: "howItWorks.steps.1",
    visual: "learn",
    tags: ["howItWorks.steps.1.tag.1", "howItWorks.steps.1.tag.2", "howItWorks.steps.1.tag.3"],
  },
  {
    key: "howItWorks.steps.2",
    visual: "catch",
    reverse: true,
    tags: ["howItWorks.steps.2.tag.1", "howItWorks.steps.2.tag.2", "howItWorks.steps.2.tag.3", "howItWorks.steps.2.tag.4"],
  },
  {
    key: "howItWorks.steps.3",
    visual: "understand",
    tags: ["howItWorks.steps.3.tag.1", "howItWorks.steps.3.tag.2", "howItWorks.steps.3.tag.3", "howItWorks.steps.3.tag.4"],
  },
  {
    key: "howItWorks.steps.4",
    visual: "execute",
    reverse: true,
    tags: ["howItWorks.steps.4.tag.1", "howItWorks.steps.4.tag.2", "howItWorks.steps.4.tag.3"],
  },
  {
    key: "howItWorks.steps.5",
    visual: "report",
    tags: ["howItWorks.steps.5.tag.1", "howItWorks.steps.5.tag.2", "howItWorks.steps.5.tag.3"],
  },
];

const channelEvents = [
  {
    brand: "line",
    label: "LINE",
    position: "left-[57%] top-[64px] w-14",
    delay: 0.2,
  },
  {
    brand: "whatsapp",
    label: "WhatsApp",
    position: "left-[59%] top-[318px] w-36",
    delay: 0.7,
  },
  {
    brand: "email",
    label: "Email",
    position: "left-[71%] top-[30px] w-12",
    delay: 1.15,
  },
  {
    brand: "forms",
    label: "Forms",
    position: "left-[56%] top-[252px] w-12",
    delay: 1.6,
  },
  {
    brand: "sms",
    label: "SMS",
    position: "right-[8%] top-[292px] w-14",
    delay: 2.05,
  },
] as const;

const channelLinks = [
  { x1: "41%", y1: "43%" },
  { x1: "62%", y1: "21%" },
  { x1: "70%", y1: "80%" },
  { x1: "76%", y1: "13%" },
  { x1: "61%", y1: "64%" },
  { x1: "89%", y1: "74%" },
] as const;

const UNDERSTAND_CYCLE_SECONDS = 4.2;
const understandTimeline = (...seconds: number[]) => seconds.map((value) => Number((value / UNDERSTAND_CYCLE_SECONDS).toFixed(3)));

const understandFields = [
  {
    key: "time",
    label: "howItWorks.visual.understand.fieldTime",
    value: "howItWorks.visual.understand.timeValue",
    color: "#A78BFA",
    bg: "rgba(82,35,177,.08)",
    icon: CalendarCheck,
    appearAt: 1,
  },
  {
    key: "doctor",
    label: "howItWorks.visual.understand.fieldDoctor",
    value: "howItWorks.visual.understand.doctorValue",
    color: "#A78BFA",
    bg: "rgba(82,35,177,.08)",
    icon: Bot,
    appearAt: 1.55,
  },
  {
    key: "service",
    label: "howItWorks.visual.understand.fieldService",
    value: "howItWorks.visual.understand.serviceValue",
    color: "#A78BFA",
    bg: "rgba(82,35,177,.08)",
    icon: Star,
    appearAt: 2,
  },
  {
    key: "phone",
    label: "howItWorks.visual.understand.fieldPhone",
    value: "howItWorks.visual.understand.phoneValue",
    color: "#A78BFA",
    bg: "rgba(82,35,177,.08)",
    icon: Phone,
    appearAt: 3.55,
  },
] as const;

function Badge({ children }: { children: string }) {
  return (
    <span className="inline-flex w-fit rounded-full bg-[#f3eefa] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#5223b1]">
      {children}
    </span>
  );
}

function FeatureTag({ children }: { children: string }) {
  return (
    <span className="rounded-full border border-[#e7dce7] bg-white px-3 py-1 text-[11px] font-bold text-[#775f78]">
      {children}
    </span>
  );
}

function ChannelBrandIcon({ brand }: { brand: (typeof channelEvents)[number]["brand"] }) {
  if (brand === "whatsapp") {
    return (
      <span className="flex items-center gap-1.5 text-[#20c65a]">
        <svg aria-hidden="true" viewBox="0 0 44 44" className="h-7 w-7">
          <path fill="#20c65a" d="M22.1 5.8c-8.9 0-16.1 6.72-16.1 15.02 0 2.92.9 5.64 2.44 7.94L6 38.2l9.74-2.26a17.07 17.07 0 0 0 6.36 1.22c8.9 0 16.12-6.72 16.12-15.02S31 5.8 22.1 5.8Z" />
          <path fill="#fff" d="M29.56 25.54c-.37-.18-2.2-1.04-2.54-1.16-.34-.12-.59-.18-.83.18-.24.36-.95 1.16-1.17 1.4-.21.24-.43.27-.8.09-.37-.18-1.56-.55-2.97-1.77a10.9 10.9 0 0 1-2.05-2.43c-.21-.36-.02-.55.16-.73.17-.16.37-.43.55-.64.18-.21.24-.36.37-.61.12-.24.06-.46-.03-.64-.09-.18-.83-1.92-1.14-2.63-.3-.69-.61-.6-.83-.61h-.71c-.24 0-.64.09-.98.43-.34.36-1.29 1.22-1.29 2.97s1.32 3.46 1.5 3.7c.18.24 2.59 3.79 6.28 5.32.88.36 1.56.58 2.1.74.88.27 1.68.23 2.31.14.71-.1 2.2-.86 2.5-1.7.31-.84.31-1.55.21-1.7-.09-.15-.34-.24-.71-.42Z" />
        </svg>
        <span className="text-sm font-extrabold leading-none">WhatsApp</span>
      </span>
    );
  }

  if (brand === "line") {
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-[#06c755] shadow-[0_8px_18px_rgba(6,199,85,.22)]">
        <svg aria-hidden="true" viewBox="0 0 44 44" className="h-9 w-9">
          <path fill="#fff" d="M22 9.6c-8 0-14.5 4.8-14.5 10.8 0 5.4 5.2 9.9 12.1 10.7.47.1 1.11.3 1.27.68.14.34.09.86.04 1.2l-.2 1.14c-.06.34-.28 1.31 1.25.72 1.53-.6 8.25-4.54 11.26-7.78 2.09-2.14 3.24-4.33 3.24-6.68C36.5 14.4 30 9.6 22 9.6Z" />
          <text x="22" y="22.7" textAnchor="middle" fontSize="7.7" fontWeight="900" fill="#06c755" fontFamily="Arial, sans-serif">
            LINE
          </text>
        </svg>
      </span>
    );
  }

  const iconClass = "h-5 w-5";
  const map = {
    email: { icon: Mail, bg: "bg-[#e8f1ff]", color: "text-[#2563eb]" },
    forms: { icon: FileText, bg: "bg-[#f3eefa]", color: "text-[#5223b1]" },
    sms: { icon: MessageSquare, bg: "bg-[#fff2f8]", color: "text-[#e9308a]" },
  } as const;
  const item = map[brand];
  const Icon = item.icon;

  return (
    <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${item.bg} ${item.color}`}>
      <Icon className={iconClass} />
    </span>
  );
}

function PluginMark({ className = "" }: { className?: string }) {
  return (
    <span className={`relative flex items-center justify-center rounded-[5px] bg-white shadow-sm ${className}`}>
      <span className="absolute left-[18%] top-[24%] h-[28%] w-[60%] rounded-[3px] bg-[#7f7cf6]" />
      <span className="absolute bottom-[23%] left-[18%] h-[28%] w-[60%] rounded-[3px] bg-[#e9d7ff]" />
      <span
        className="absolute right-[10%] top-[32%] h-[36%] w-[34%] bg-[#ff5a7a]"
        style={{ clipPath: "polygon(0 0, 100% 50%, 0 100%)" }}
      />
    </span>
  );
}

function getLearnCursorPoint(
  phase: number,
  layout: { frameWidth: number; urlStartX: number; urlEndX: number; urlY: number; pluginX: number; pluginY: number }
) {
  if (phase < 2.3 || phase >= 8.8) return null;

  const sidebarX = layout.frameWidth > 0 ? layout.frameWidth * 0.85 : 600;
  const exploreButtonY = LEARN_BROWSER_TOOLBAR_HEIGHT + LEARN_EXPLORE_BUTTON_TOP + LEARN_BUTTON_CENTER_OFFSET;
  const confirmButtonY = LEARN_BROWSER_TOOLBAR_HEIGHT + LEARN_CONFIRM_BUTTON_TOP + LEARN_BUTTON_CENTER_OFFSET;
  const points = [
    { at: 2.3, x: layout.urlEndX + 6, y: layout.urlY, opacity: 0 },
    { at: 2.45, x: layout.urlEndX + 6, y: layout.urlY, opacity: 1 },
    { at: LEARN_PLUGIN_CURSOR_MOVE_START, x: layout.urlEndX + 6, y: layout.urlY, opacity: 1 },
    { at: LEARN_PLUGIN_CURSOR_LAND, x: layout.pluginX, y: layout.pluginY, opacity: 1 },
    { at: 4.6, x: layout.pluginX, y: layout.pluginY, opacity: 1 },
    { at: 4.75, x: sidebarX, y: exploreButtonY, opacity: 1 },
    { at: 6.8, x: sidebarX, y: exploreButtonY, opacity: 1 },
    { at: 7.2, x: sidebarX, y: confirmButtonY, opacity: 1 },
    { at: 8.8, x: sidebarX, y: confirmButtonY, opacity: 1 },
  ];

  for (let index = 0; index < points.length - 1; index += 1) {
    const from = points[index];
    const to = points[index + 1];

    if (phase >= from.at && phase <= to.at) {
      const progress = progressBetween(phase, from.at, to.at);
      return {
        x: lerp(from.x, to.x, progress),
        y: lerp(from.y, to.y, progress),
        opacity: lerp(from.opacity, to.opacity, progress),
      };
    }
  }

  return points[points.length - 1];
}

function BrowserFrame({ children, phase, lang = "en" }: { children: ReactNode; phase: number; lang?: Lang }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const urlTextRef = useRef<HTMLSpanElement>(null);
  const pluginIconRef = useRef<HTMLDivElement>(null);
  const [cursorLayout, setCursorLayout] = useState({
    frameWidth: 680,
    urlStartX: 148,
    urlEndX: 148,
    urlY: 30,
    pluginX: 590,
    pluginY: 30,
  });
  const urlProgress = progressBetween(phase, 0.5, 2.1, false);
  const typedUrl = LEARN_URL.slice(0, Math.round(LEARN_URL.length * urlProgress));
  const caretVisible = phase >= 0.3 && phase < 2.1 && Math.floor(phase * 5) % 2 === 0;
  const urlFlash = fadeInOut(phase, 2.1, 2.16, 2.24, 2.3);
  const addressFocused = phase >= 0.3 && phase < 2.3;
  const loadingOpacity = fadeInOut(phase, 2.3, 2.34, 2.62, 2.7);
  const loadingProgress = progressBetween(phase, 2.3, 2.7);
  const pluginHovered = phase >= LEARN_PLUGIN_HOVER_START && phase < LEARN_PLUGIN_PRESS_END;
  const pluginPressed = phase >= LEARN_PLUGIN_PRESS_START && phase < LEARN_PLUGIN_PRESS_END;
  const cursorPoint = getLearnCursorPoint(phase, cursorLayout);
  const cursorPressed = (phase >= LEARN_PLUGIN_PRESS_START && phase < LEARN_PLUGIN_PRESS_END) || (phase >= 4.75 && phase < 4.85) || (phase >= 7.2 && phase < 7.4);

  useLayoutEffect(() => {
    const updateCursorLayout = () => {
      const frame = frameRef.current;
      const urlText = urlTextRef.current;
      const pluginIcon = pluginIconRef.current;
      if (!frame || !urlText) return;

      const frameRect = frame.getBoundingClientRect();
      const urlRect = urlText.getBoundingClientRect();
      const iconRect = pluginIcon?.getBoundingClientRect();

      setCursorLayout({
        frameWidth: frameRect.width,
        urlStartX: urlRect.left - frameRect.left,
        urlEndX: urlRect.right - frameRect.left,
        urlY: urlRect.top - frameRect.top + urlRect.height / 2,
        pluginX: iconRect ? iconRect.left - frameRect.left + iconRect.width / 2 : 590,
        pluginY: iconRect ? iconRect.top - frameRect.top + iconRect.height / 2 : 30,
      });
    };

    updateCursorLayout();

    const observer = new ResizeObserver(updateCursorLayout);
    if (frameRef.current) observer.observe(frameRef.current);
    if (urlTextRef.current) observer.observe(urlTextRef.current);
    if (pluginIconRef.current) observer.observe(pluginIconRef.current);
    window.addEventListener("resize", updateCursorLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateCursorLayout);
    };
  }, [typedUrl]);

  return (
    <div
      ref={frameRef}
      className="relative overflow-hidden rounded-[22px] border border-[#e7dce7] bg-white shadow-[0_24px_70px_rgba(46,22,152,.08)]"
      style={{
        opacity: phase < 0.3 ? progressBetween(phase, 0, 0.3) : phase >= 8.8 ? 1 - progressBetween(phase, 8.8, 9.5) : 1,
        transform: phase >= 8.1 ? `translateY(${progressBetween(phase, 8.1, 8.8) * 3}px)` : "translateY(0)",
      }}
    >
      <div className="relative flex items-center gap-2 border-b border-[#efe7ef] bg-[#fbf9fc] px-5 py-4">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff6b6b]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#f7c948]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#36b37e]" />
        <div
          className="ml-4 flex h-7 flex-1 items-center rounded-full bg-white px-4 text-[11px] font-semibold text-[#775f78] transition-shadow"
          style={{
            boxShadow: addressFocused ? "0 0 0 4px rgba(82,35,177,.08)" : "0 0 0 0 rgba(82,35,177,0)",
          }}
        >
          <span className="relative flex min-w-0 flex-1 items-center">
            <span
              ref={urlTextRef}
              className="inline-block overflow-hidden whitespace-nowrap"
              style={{
                color: urlFlash > 0 ? "#5223b1" : "#775f78",
                textShadow: urlFlash > 0 ? "0 0 14px rgba(82,35,177,.16)" : "none",
              }}
            >
              {typedUrl}
            </span>
            <span
              className="ml-0.5 inline-block h-3.5 w-px bg-[#5223b1]"
              style={{ opacity: caretVisible ? 1 : 0 }}
            />
            {!typedUrl && (
              <span className="absolute left-0 text-[#b7a8ba]">
                {t(lang, "howItWorks.visual.browser.search")}
              </span>
            )}
          </span>
          <Star size={15} className="ml-3 flex-shrink-0 text-[#3f3f42]/75" />
        </div>
        <div
          className="ml-1 hidden h-8 items-center gap-3 rounded-full bg-[#2f2f31]/80 px-3 sm:inline-flex"
          style={{ boxShadow: pluginHovered ? "0 10px 22px rgba(40,22,41,.16)" : "none" }}
        >
          <div
            ref={pluginIconRef}
            className="relative flex h-[22px] w-[22px] items-center justify-center"
            style={{
              transform: `translateX(${pluginPressed ? -1 : 0}px) scale(${pluginPressed ? 0.94 : pluginHovered ? 1.08 : 1})`,
              transition: "transform 120ms ease-out",
            }}
          >
            <PluginMark className="h-full w-full" />
          </div>
            <Puzzle size={17} className="text-white/82" />
            <span className="h-5 w-px bg-white/18" />
        </div>
        <div
          className="pointer-events-none absolute bottom-0 left-0 h-[2px] rounded-full bg-[#5223b1]"
          style={{ opacity: loadingOpacity, width: `${loadingProgress * 100}%` }}
        />
      </div>
      {children}
      {cursorPoint && (
        <div
          className="pointer-events-none absolute left-0 top-0 z-[120] hidden text-[#5223b1] drop-shadow-[0_8px_12px_rgba(82,35,177,.22)] sm:block"
          style={{
            opacity: cursorPoint.opacity,
            transform: `translate3d(${cursorPoint.x}px, ${cursorPoint.y}px, 0) scale(${cursorPressed ? 0.88 : 1})`,
            transition: "transform 90ms linear, opacity 120ms ease-out",
          }}
        >
          <span className="block -translate-x-[3px] -translate-y-[2px]">
            <MousePointer2 size={21} fill="white" strokeWidth={2.4} />
          </span>
        </div>
      )}
    </div>
  );
}

function LearnVisual({ lang }: { lang: Lang }) {
  const phase = useLoopPhase(LEARN_CYCLE_SECONDS);
  const workflowRules = [
    "howItWorks.visual.learn.rule1",
    "howItWorks.visual.learn.rule2",
    "howItWorks.visual.learn.rule3",
  ];
  const skeletonRows = [
    { top: "72%", bottom: "44%", tint: "#f5eefb" },
    { top: "58%", bottom: "76%", tint: "#eef3ff" },
    { top: "82%", bottom: "52%", tint: "#f3f1fb" },
    { top: "66%", bottom: "62%", tint: "#fff2f8" },
  ];
  const shimmer = `${((phase % 1.8) / 1.8) * 100}% 50%`;
  const panelProgress = progressBetween(phase, LEARN_PLUGIN_PANEL_START, LEARN_PLUGIN_PANEL_END);
  const googleOpacity = 1 - progressBetween(phase, 2.3, 2.7);
  const bookingOpacity = progressBetween(phase, 2.7, 3.2);
  const introLogoOpacity = fadeInOut(phase, 3.95, 4.3, 4.85, 5.25);
  const introButtonOpacity = fadeInOut(phase, 4.15, 4.5, 4.85, 5.25);
  const introCommandOpacity = fadeInOut(phase, 4.25, 4.6, 4.85, 5.25);
  const explorePressed = phase >= 4.75 && phase < 4.85;
  const dockProgress = progressBetween(phase, 4.85, 5.65);
  const titleOpacity = fadeInOut(phase, 5.65, 5.85, 8.1, 8.8);
  const dockedLogoOpacity = dockProgress > 0 ? (phase < 8.1 ? 1 : titleOpacity) : introLogoOpacity;
  const rulesOpacity = fadeInOut(phase, 5.65, 5.95, 7.4, 7.7);
  const confirmOpacity = fadeInOut(phase, 6.8, 7.2, 7.4, 7.7);
  const confirmPressed = phase >= 7.2 && phase < 7.4;
  const confirmDone = phase >= 7.4 && phase < 7.7;
  const toastOpacity = fadeInOut(phase, 7.7, 8.1, 8.8, 9.5);
  const statusOpacity = fadeInOut(phase, 4.85, 5.25, 7.7, 8.1);
  const initialBreath = fadeInOut(phase, 4.85, 5.25, 5.65, 6.0);
  const exploringBreath = statusOpacity * (0.18 + ((Math.sin((phase - 4.85) * Math.PI * 1.6) + 1) / 2) * 0.14);
  const browserGlow = Math.max(initialBreath * 0.42, exploringBreath);
  const ruleStarts = [5.65, 5.92, 6.18];
  const ruleProgress = (index: number) => progressBetween(phase, ruleStarts[index], ruleStarts[index] + 0.2);
  const ruleCheck = (index: number) => progressBetween(phase, ruleStarts[index] + 0.2, ruleStarts[index] + 0.38);

  return (
    <BrowserFrame phase={phase} lang={lang}>
      <div className="relative h-[430px] overflow-hidden bg-white">
        <div
          className="absolute inset-y-0 left-0 overflow-hidden bg-white"
          style={{
            right: `${panelProgress * 30}%`,
            transformOrigin: "left center",
            willChange: "right",
          }}
        >
          <div
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              opacity: browserGlow,
              background:
                "radial-gradient(circle at 0% 0%, rgba(123,47,208,.2) 0%, rgba(245,238,255,.16) 24%, transparent 48%), radial-gradient(circle at 100% 100%, rgba(123,47,208,.14) 0%, rgba(245,238,255,.12) 22%, transparent 46%)",
              boxShadow: `inset 0 0 ${18 + browserGlow * 12}px rgba(123,47,208,${0.12 + browserGlow * 0.12})`,
            }}
          />

          <div
            className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center p-8"
            style={{ opacity: googleOpacity, transform: `scale(${0.98 + googleOpacity * 0.02})` }}
          >
            <div className="flex w-full max-w-[430px] items-center justify-center">
              <div className="flex h-11 w-full items-center rounded-full border border-[#e7dce7] bg-white px-4 shadow-sm">
                <span className="mr-3 h-2.5 w-2.5 rounded-full border-2 border-[#775f78]/45" />
                <span className="text-xs font-semibold text-[#775f78]">{t(lang, "howItWorks.visual.browser.search")}</span>
                <span className="ml-auto text-xs font-extrabold text-[#5223b1]">Google</span>
              </div>
            </div>
          </div>

          <div
            className="relative z-10 h-full p-7"
            style={{ opacity: bookingOpacity, transform: `translateY(${(1 - bookingOpacity) * 8}px)` }}
          >
            <div className="relative h-full w-full overflow-hidden">
              <div
                className="relative z-10 h-full space-y-4"
                style={{ transform: `translateY(${-progressBetween(phase, 4.85, 5.65) * 40}px)` }}
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#f3eefa]">
                    <CalendarCheck size={16} className="text-[#5223b1]" />
                  </div>
                  <div className="text-sm font-extrabold text-[#100f12]">{t(lang, "howItWorks.visual.learn.bookingSystem")}</div>
                </div>

                {skeletonRows.map((row, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div
                      className="h-11 w-11 shrink-0 rounded-xl"
                      style={{
                        backgroundImage: `linear-gradient(90deg, ${row.tint} 0%, #ffffff 34%, ${row.tint} 68%, #f8f4fb 100%)`,
                        backgroundSize: "220% 100%",
                        backgroundPosition: shimmer,
                      }}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div
                        className="h-3 rounded-full"
                        style={{
                          width: row.top,
                          backgroundImage: `linear-gradient(90deg, #f2ebfa 0%, #ffffff 36%, ${row.tint} 70%, #efe7fb 100%)`,
                          backgroundSize: "240% 100%",
                          backgroundPosition: shimmer,
                        }}
                      />
                      <div
                        className="h-2.5 rounded-full"
                        style={{
                          width: row.bottom,
                          backgroundImage: `linear-gradient(90deg, #fbf0f7 0%, #ffffff 36%, #eef3ff 72%, #f6effb 100%)`,
                          backgroundSize: "240% 100%",
                          backgroundPosition: shimmer,
                        }}
                      />
                    </div>
                  </div>
                ))}

                <div className="flex justify-end pt-1">
                  <div
                    className="h-9 w-28 rounded-xl bg-[#dcfce7]/75"
                    style={{ boxShadow: browserGlow > 0 ? "0 12px 26px rgba(5,150,105,.08)" : "none" }}
                  />
                </div>
              </div>

              <div
                className="absolute inset-x-9 bottom-6 z-20 flex items-center gap-2 rounded-full bg-white/92 px-3 py-2 text-[11px] font-extrabold text-[#775f78] shadow-[0_8px_18px_rgba(82,35,177,.08)] backdrop-blur-sm"
                style={{ opacity: statusOpacity, transform: `translateY(${(1 - statusOpacity) * 8}px)` }}
              >
                <motion.span
                  className="h-2.5 w-2.5 rounded-full bg-[#e5252a]"
                  animate={{ scale: [1, 1.38, 1], opacity: [0.5, 1, 0.5], boxShadow: ["0 0 0 rgba(229,37,42,0)", "0 0 12px rgba(229,37,42,.55)", "0 0 0 rgba(229,37,42,0)"] }}
                  transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
                />
                {t(lang, "howItWorks.visual.learn.status")}
              </div>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 right-0 top-0 z-30 w-[30%] min-w-[188px] overflow-hidden border-l border-[#efe7ef] bg-white"
          style={{
            opacity: panelProgress,
            transform: `translate3d(${(1 - panelProgress) * 100}%, 0, 0)`,
          }}
        >
          <div className="relative h-full p-4">
            <div
              className="absolute z-20 flex h-12 w-12 items-center justify-center"
              style={{
                opacity: dockedLogoOpacity,
                left: `${lerp(50, 0, dockProgress)}%`,
                top: `${lerp(LEARN_PLUGIN_LOGO_TOP, LEARN_PLUGIN_DOCK_TOP, dockProgress)}px`,
                transform: `translateX(${lerp(-50, 0, dockProgress)}%) scale(${lerp(1, LEARN_PLUGIN_DOCK_SCALE, dockProgress)})`,
              }}
            >
              <PluginMark className="h-12 w-12 rounded-[14px]" />
            </div>

            <div
              className="absolute left-0 right-0 top-0 z-10 flex h-12 items-center pl-10 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#5223b1]"
              style={{ opacity: titleOpacity, transform: `translateY(${(1 - titleOpacity) * 4}px)` }}
            >
              {t(lang, "howItWorks.visual.learn.plugin")}
            </div>

            <div
              className="absolute inset-x-4"
              style={{
                top: LEARN_EXPLORE_BUTTON_TOP,
                opacity: introButtonOpacity,
                transform: `translateY(${(1 - introButtonOpacity) * 8}px)`,
              }}
            >
              <button
                type="button"
                className="inline-flex min-h-9 w-full items-center justify-center rounded-xl bg-[#5223b1] text-xs font-extrabold text-white"
                style={{
                  transform: `translateX(${explorePressed ? -1 : 0}px) scale(${explorePressed ? 0.94 : phase >= 4.6 && phase < 4.75 ? 1.03 : 1})`,
                  opacity: explorePressed ? 0.84 : 1,
                }}
              >
                {t(lang, "howItWorks.visual.learn.start")}
              </button>
            </div>

            <div
              className="absolute inset-x-4 bottom-4 flex h-8 items-center gap-2 rounded-xl border border-[#e7dce7] bg-[#fcfbfd] px-3 text-[10px] font-semibold text-[#775f78]"
              style={{ opacity: introCommandOpacity, transform: `translateY(${(1 - introCommandOpacity) * 8}px)` }}
            >
              {t(lang, "howItWorks.visual.learn.command")}
              <Send size={12} className="ml-auto text-[#5223b1]" />
            </div>

            <div
              className="absolute inset-x-4 top-12 space-y-2.5"
              style={{
                opacity: rulesOpacity,
                transform: `translateY(${(1 - rulesOpacity) * 8}px)`,
              }}
            >
              {workflowRules.map((item, index) => {
                const appear = ruleProgress(index);
                const checked = ruleCheck(index);
                const rulePulse = phase >= 6.3 && phase < 6.8 ? 1 : 0;

                return (
                  <div
                    key={item}
                    className="flex items-center gap-2.5 rounded-xl border px-2.5 py-2"
                    style={{
                      opacity: appear,
                      transform: `translateY(${(1 - appear) * 6}px)`,
                      borderColor: checked > 0.5 || rulePulse ? "#a7f3d0" : "#e7dce7",
                      backgroundColor: checked > 0.5 || rulePulse ? "#f0fdf4" : "#ffffff",
                    }}
                  >
                    <span
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full border bg-white"
                      style={{
                        borderColor: checked > 0.5 ? "#059669" : "#d8cbd8",
                        backgroundColor: checked > 0.5 ? "#059669" : "#ffffff",
                        transform: `scale(${1 + checked * 0.08})`,
                      }}
                    >
                      <CheckCircle2 size={12} className="text-white" />
                    </span>
                    <span className="text-xs font-semibold text-[#3a383f]">{t(lang, item)}</span>
                  </div>
                );
              })}
            </div>

            <div
              className="absolute inset-x-4"
              style={{
                top: LEARN_CONFIRM_BUTTON_TOP,
                opacity: confirmOpacity,
                transform: `translateY(${(1 - confirmOpacity) * 6}px)`,
              }}
            >
              <button
                type="button"
                className="inline-flex min-h-9 w-full items-center justify-center gap-1.5 rounded-xl text-xs font-extrabold"
                style={{
                  backgroundColor: confirmDone ? "#ecfdf5" : "#281629",
                  border: confirmDone ? "1px solid #a7f3d0" : "1px solid transparent",
                  color: confirmDone ? "#047857" : "#ffffff",
                  transform: `translateX(${confirmPressed ? -1 : 0}px) scale(${confirmPressed ? 0.94 : 1})`,
                  boxShadow: confirmPressed ? "0 0 20px rgba(82,35,177,.18)" : "none",
                }}
              >
                {confirmDone && <CheckCircle2 size={14} />}
                {t(lang, "howItWorks.visual.learn.confirm")}
              </button>
            </div>

            <div
              className="absolute inset-x-4 bottom-12 rounded-xl border border-[#a7f3d0] bg-[#f0fdf4] px-3 py-2 text-center text-[11px] font-extrabold text-[#047857] shadow-sm"
              style={{ opacity: toastOpacity, transform: `translateY(${(1 - toastOpacity) * 8}px)` }}
            >
              <CheckCircle2 size={13} className="mr-1 inline-block" />
              {t(lang, "howItWorks.visual.learn.done")}
            </div>
          </div>
        </div>
      </div>
    </BrowserFrame>
  );
}

function CatchVisual({ lang }: { lang: Lang }) {
  return (
    <div className="relative h-[430px] overflow-hidden rounded-[24px] border border-[#e7dce7] bg-white p-5 shadow-[0_24px_70px_rgba(46,22,152,.08)]">
      <div className="relative z-20 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#775f78]">
            {t(lang, "howItWorks.visual.catch.hub")}
          </div>
          <div className="mt-1 text-sm font-extrabold text-[#100f12]">{t(lang, "howItWorks.visual.catch.coverage")}</div>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-[#e1d6f3] bg-[#f3eefa] px-3 py-1.5 text-[11px] font-extrabold text-[#5223b1]">
          <motion.span
            className="h-2 w-2 rounded-full bg-[#7b2fd0]"
            animate={{ scale: [1, 1.45, 1], opacity: [0.55, 1, 0.55] }}
            transition={{ duration: 1.05, repeat: Infinity, ease: "easeInOut" }}
          />
          {t(lang, "howItWorks.visual.catch.live")}
        </div>
      </div>

      <motion.div
        className="absolute left-5 top-[88px] z-30 min-h-[224px] w-[46%] rounded-[22px] border border-[#e7dce7] bg-white p-4 shadow-[0_18px_42px_rgba(46,22,152,.1)]"
        animate={{ y: [0, -4, 0], boxShadow: ["0 18px 42px rgba(46,22,152,.1)", "0 24px 48px rgba(82,35,177,.14)", "0 18px 42px rgba(46,22,152,.1)"] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.1em] text-[#5223b1]">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3eefa]">
              <Phone size={15} />
            </span>
            {t(lang, "howItWorks.visual.catch.call")}
          </div>
          <span className="rounded-full bg-[#f3eefa] px-2.5 py-1 text-[10px] font-extrabold text-[#5223b1]">
            {t(lang, "howItWorks.visual.catch.answered")}
          </span>
        </div>
        <div className="space-y-3">
          <div className="max-w-[65%]">
            <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">
              {t(lang, "howItWorks.visual.catch.callerTime")}
            </div>
            <div className="break-words rounded-2xl rounded-bl-md bg-[#f8f4fb] px-3.5 py-3 text-sm font-bold leading-relaxed text-[#3a383f]">
              {t(lang, "howItWorks.visual.catch.callText")}
            </div>
          </div>
          <div className="ml-auto max-w-[65%]">
            <div className="mb-1 text-right text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">
              {t(lang, "howItWorks.visual.catch.replyTime")}
            </div>
            <div className="break-words rounded-2xl rounded-br-md bg-[#281629] px-3.5 py-3 text-sm font-bold leading-relaxed text-white">
              {t(lang, "howItWorks.visual.catch.replyText")}
            </div>
          </div>
        </div>
      </motion.div>

      <svg aria-hidden="true" className="pointer-events-none absolute inset-0 z-[5] h-full w-full">
        {channelLinks.map((line, index) => (
          <motion.line
            key={`${line.x1}-${line.y1}`}
            x1={line.x1}
            y1={line.y1}
            x2="82%"
            y2="42%"
            stroke="#cdb8e8"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="4 7"
            animate={{ opacity: [0.16, 0.46, 0.16], strokeDashoffset: [20, 0, -20] }}
            transition={{ duration: 3.2, repeat: Infinity, delay: index * 0.22, ease: "linear" }}
          />
        ))}
      </svg>

      <div className="absolute right-8 top-[94px] z-10 flex h-[168px] w-[168px] items-center justify-center rounded-full bg-[#fcfbfd]">
        <motion.span
          className="absolute h-full w-full rounded-full border border-[#d8c6ef]"
          animate={{ scale: [0.72, 1.08], opacity: [0.44, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
        />
        <motion.span
          className="absolute h-[78%] w-[78%] rounded-full border border-[#d9f6e6]"
          animate={{ scale: [0.76, 1.14], opacity: [0.5, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 0.8 }}
        />
        <div className="relative z-10 flex h-[108px] w-[108px] flex-col items-center justify-center rounded-[28px] border border-[#e7dce7] bg-white text-center shadow-[0_18px_44px_rgba(46,22,152,.09)]">
          <Bot size={23} className="text-[#5223b1]" />
          <div className="mt-2 text-[11px] font-extrabold leading-tight text-[#100f12]">{t(lang, "howItWorks.visual.catch.listening")}</div>
          <div className="mt-1 text-[10px] font-bold text-[#775f78]">{t(lang, "howItWorks.visual.catch.channelOnline")}</div>
        </div>
      </div>

      {channelEvents.map((item) => (
        <motion.div
          key={item.label}
          aria-label={item.label}
          className={["absolute z-20 flex h-12 items-center justify-center rounded-2xl border border-[#efe7ef] bg-white/95 shadow-[0_14px_34px_rgba(46,22,152,.08)] backdrop-blur", item.position].join(" ")}
          animate={{
            opacity: [0.72, 1, 1, 0.76, 0.72],
            y: [10, 0, 0, -6, 10],
            scale: [0.98, 1, 1, 0.99, 0.98],
          }}
          transition={{ duration: 5.4, repeat: Infinity, delay: item.delay, ease: "easeInOut" }}
        >
          <ChannelBrandIcon brand={item.brand} />
        </motion.div>
      ))}

    </div>
  );
}

function UnderstandVisual({ lang }: { lang: Lang }) {
  return (
    <div className="relative min-h-[430px] overflow-hidden rounded-[24px] border border-[#e7dce7] bg-white p-5 shadow-[0_24px_70px_rgba(46,22,152,.08)]">
      <div className="relative z-10 grid gap-5 lg:grid-cols-[1.05fr_.95fr]">
        <div className="relative min-h-[390px] overflow-hidden rounded-[22px] border border-[#efe7ef] bg-[#fcfbfd] p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[#775f78]">
              {t(lang, "howItWorks.visual.understand.live")}
            </div>
            <div className="rounded-full bg-[#f3eefa] px-3 py-1 text-[10px] font-extrabold text-[#5223b1]">
              {t(lang, "howItWorks.visual.understand.extracting")}
            </div>
          </div>

          <div className="space-y-2.5">
            <motion.div
              className="max-w-[82%]"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.65 }}
              transition={{ duration: 0.32, delay: 0.1, ease: "easeOut" }}
            >
              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">
                {t(lang, "howItWorks.visual.understand.customer")}
              </div>
              <div className="break-words rounded-2xl rounded-bl-md bg-[#f8f4fb] px-3.5 py-2.5 text-sm font-bold leading-[1.78] text-[#3a383f]">
                <span>{t(lang, "howItWorks.visual.understand.userPrefix")}</span>
                <motion.span
                  className="mx-0.5 px-0.5 text-[#5223b1]"
                  initial={{ boxShadow: "inset 0 0 0 rgba(82,35,177,0)" }}
                  whileInView={{ boxShadow: "inset 0 -6px 0 rgba(82,35,177,.14)" }}
                  viewport={{ once: true, amount: 0.65 }}
                  transition={{ duration: 0.28, delay: 0.62, ease: "easeOut" }}
                >
                  {t(lang, "howItWorks.visual.understand.timeValue")}
                </motion.span>
                <span>{t(lang, "howItWorks.visual.understand.doctorPrefix")}</span>
                <motion.span
                  className="mx-0.5 px-0.5 text-[#5223b1]"
                  initial={{ boxShadow: "inset 0 0 0 rgba(82,35,177,0)" }}
                  whileInView={{ boxShadow: "inset 0 -6px 0 rgba(82,35,177,.14)" }}
                  viewport={{ once: true, amount: 0.65 }}
                  transition={{ duration: 0.28, delay: 0.98, ease: "easeOut" }}
                >
                  {t(lang, "howItWorks.visual.understand.doctorValue")}
                </motion.span>
                <span>{t(lang, "howItWorks.visual.understand.servicePrefix")}</span>
                <motion.span
                  className="mx-0.5 px-0.5 text-[#5223b1]"
                  initial={{ boxShadow: "inset 0 0 0 rgba(82,35,177,0)" }}
                  whileInView={{ boxShadow: "inset 0 -6px 0 rgba(82,35,177,.14)" }}
                  viewport={{ once: true, amount: 0.65 }}
                  transition={{ duration: 0.28, delay: 1.34, ease: "easeOut" }}
                >
                  {t(lang, "howItWorks.visual.understand.serviceValue")}
                </motion.span>
              </div>
            </motion.div>

            <motion.div
              className="ml-auto max-w-[72%] text-right"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.65 }}
              transition={{ duration: 0.32, delay: 1.55, ease: "easeOut" }}
            >
              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">
                {t(lang, "howItWorks.visual.understand.clawease")}
              </div>
              <div className="break-words rounded-2xl rounded-br-md bg-[#281629] px-3.5 py-2.5 text-sm font-bold leading-snug text-white">
                {t(lang, "howItWorks.visual.understand.agentAsk")}
              </div>
            </motion.div>

            <motion.div
              className="max-w-[58%]"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.65 }}
              transition={{ duration: 0.32, delay: 2.15, ease: "easeOut" }}
            >
              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">
                {t(lang, "howItWorks.visual.understand.customer")}
              </div>
              <div className="break-words rounded-2xl rounded-bl-md bg-[#f8f4fb] px-3.5 py-2.5 text-sm font-bold text-[#3a383f]">
                <motion.span
                  className="px-0.5 text-[#5223b1]"
                  initial={{ boxShadow: "inset 0 0 0 rgba(82,35,177,0)" }}
                  whileInView={{ boxShadow: "inset 0 -6px 0 rgba(82,35,177,.14)" }}
                  viewport={{ once: true, amount: 0.65 }}
                  transition={{ duration: 0.28, delay: 2.45, ease: "easeOut" }}
                >
                  {t(lang, "howItWorks.visual.understand.phoneValue")}
                </motion.span>
              </div>
            </motion.div>

            <motion.div
              className="ml-auto max-w-[70%] text-right"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.65 }}
              transition={{ duration: 0.32, delay: 2.82, ease: "easeOut" }}
            >
              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">
                {t(lang, "howItWorks.visual.understand.clawease")}
              </div>
              <div className="break-words rounded-2xl rounded-br-md bg-[#281629] px-3.5 py-2.5 text-sm font-bold leading-snug text-white">
                {t(lang, "howItWorks.visual.understand.agentConfirm")}
              </div>
            </motion.div>
          </div>
        </div>

        <div className="min-h-[390px] rounded-[22px] border border-[#efe7ef] bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[#775f78]">
                {t(lang, "howItWorks.visual.understand.structured")}
              </div>
              <motion.div
                className="mt-2 inline-flex rounded-full px-3 py-1 text-xs font-extrabold"
                initial={{ backgroundColor: "#f3f4f6", color: "#9ca3af", opacity: 0, y: 4 }}
                whileInView={{ backgroundColor: "#f5f3ff", color: "#6D28D9", opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.7 }}
                transition={{ duration: 0.26, delay: 0.55, ease: "easeOut" }}
              >
                {t(lang, "howItWorks.visual.understand.intent")}: {t(lang, "howItWorks.visual.understand.booking")}
              </motion.div>
            </div>
            <Bot size={22} className="text-[#A78BFA]" />
          </div>

          <div className="space-y-2.5">
            {understandFields.map((field) => {
              const Icon = field.icon;
              const cardDelay = field.key === "phone" ? 2.65 : field.appearAt + 0.2;

              return (
                <motion.div
                  key={field.key}
                  className="flex items-center gap-3 rounded-xl border px-3 py-2.5"
                  initial={{ borderColor: "#e7dce7", backgroundColor: "#ffffff" }}
                  whileInView={{ borderColor: "#a7f3d0", backgroundColor: "#ffffff" }}
                  viewport={{ once: true, amount: 0.7 }}
                  transition={{ duration: 0.3, delay: cardDelay, ease: "easeOut" }}
                >
                  <motion.span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                    initial={{ backgroundColor: "#f5f3f6", color: "#b8abb8" }}
                    whileInView={{ backgroundColor: field.bg, color: field.color }}
                    viewport={{ once: true, amount: 0.7 }}
                    transition={{ duration: 0.25, delay: cardDelay, ease: "easeOut" }}
                  >
                    <Icon size={16} />
                  </motion.span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">{t(lang, field.label)}</div>
                    <div className="relative mt-0.5 h-5 truncate text-sm font-extrabold text-[#100f12]">
                      <motion.span
                        className="absolute left-0 top-1.5 h-2.5 w-24 rounded-full bg-[#f0edf2]"
                        initial={{ opacity: 1 }}
                        whileInView={{ opacity: 0 }}
                        viewport={{ once: true, amount: 0.7 }}
                        transition={{ duration: 0.18, delay: cardDelay, ease: "easeOut" }}
                      />
                      <motion.span
                        className="absolute left-0 top-0"
                        initial={{ opacity: 0, y: 4 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.7 }}
                        transition={{ duration: 0.24, delay: cardDelay + 0.08, ease: "easeOut" }}
                      >
                        {t(lang, field.value)}
                      </motion.span>
                    </div>
                  </div>
                  <motion.span
                    className="text-[#059669]"
                    initial={{ opacity: 0, scale: 0.6 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.7 }}
                    transition={{ duration: 0.22, delay: cardDelay + 0.18, ease: "easeOut" }}
                  >
                    <CheckCircle2 size={17} />
                  </motion.span>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">
              {t(lang, "howItWorks.visual.understand.fields")}
              <motion.span
                initial={{ opacity: 0.45 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.7 }}
                transition={{ duration: 0.22, delay: 3.05, ease: "easeOut" }}
              >
                4/5
              </motion.span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#f0edf2]">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#A78BFA] to-[#DDD6FE]"
                initial={{ width: "0%" }}
                whileInView={{ width: "80%" }}
                viewport={{ once: true, amount: 0.7 }}
                transition={{ duration: 2.25, delay: 0.78, ease: "easeOut" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExecuteVisual({ lang }: { lang: Lang }) {
  const phase = useLoopPhase(EXECUTE_CYCLE_SECONDS);
  const browserIn = clamp01((phase - 1.7) / 0.5);
  const browserOut = clamp01((phase - 7.32) / 0.23);
  const browserScale = phase < 1.7 ? 0.12 : phase >= 7.32 ? 1 - browserOut * 0.88 : 0.12 + browserIn * 0.88;
  const browserOpacity = phase < 1.62 || phase > 7.55 ? 0 : 1;
  const browserRadius = phase >= 7.32 ? 18 + browserOut * 120 : 140 - browserIn * 122;
  const desktopOpacity = phase < 1.85 || phase > 7.34 ? 1 : 0;
  const desktopLineProgress = clamp01((phase - 0.4) / 0.5);
  const url = t(lang, "howItWorks.visual.execute.url");
  const urlProgress = clamp01((phase - 2.28) / 0.68);
  const typedUrl = phase < 2.28 ? "" : url.slice(0, Math.round(url.length * urlProgress));
  const formOpacity = clamp01((phase - 3.28) / 0.24);
  const skeletonOpacity = phase < 3 ? 0 : phase < 3.25 ? 1 : 1 - clamp01((phase - 3.25) / 0.22);
  const successProgress = clamp01((phase - 6) / 0.38);
  const successVisible = phase >= 6 && phase < 7.32;
  const submitPressed = phase >= 5.84 && phase < 6;
  const submitting = phase >= 5.94 && phase < 7.32;

  const fields = [
    { label: "howItWorks.visual.execute.customer", value: "howItWorks.visual.execute.customerValue", start: 3.8, end: 4.08, dropdown: false },
    { label: "howItWorks.visual.execute.phone", value: "howItWorks.visual.execute.phoneValue", start: 4.35, end: 4.68, dropdown: false },
    { label: "howItWorks.visual.execute.time", value: "howItWorks.visual.execute.timeValue", start: 4.86, end: 5.2, dropdown: true },
    { label: "howItWorks.visual.execute.service", value: "howItWorks.visual.execute.serviceValue", start: 5.32, end: 5.62, dropdown: true },
  ];

  const cursorPoints = [
    { at: 1.5, x: 30, y: 84 },
    { at: 1.7, x: 58, y: 48 },
    { at: 3.55, x: 25, y: 43 },
    { at: 4.22, x: 25, y: 53 },
    { at: 4.78, x: 25, y: 63 },
    { at: 5.2, x: 25, y: 63 },
    { at: 5.32, x: 25, y: 73 },
    { at: 5.62, x: 25, y: 73 },
    { at: 5.76, x: 50, y: 84 },
    { at: 6.1, x: 50, y: 84 },
  ];
  const getCursorPoint = () => {
    if (phase < cursorPoints[0].at || phase > 6.25) return null;

    for (let index = 0; index < cursorPoints.length - 1; index += 1) {
      const from = cursorPoints[index];
      const to = cursorPoints[index + 1];

      if (phase >= from.at && phase <= to.at) {
        const progress = clamp01((phase - from.at) / (to.at - from.at));
        const eased = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        return {
          x: from.x + (to.x - from.x) * eased,
          y: from.y + (to.y - from.y) * eased,
        };
      }
    }

    return cursorPoints[cursorPoints.length - 1];
  };
  const cursorPoint = getCursorPoint();

  const fieldValueFor = (field: (typeof fields)[number]) => {
    if (phase < field.start) return "";
    if (field.dropdown) return phase >= field.end ? t(lang, field.value) : "";

    const value = t(lang, field.value);
    const progress = clamp01((phase - field.start) / (field.end - field.start));
    return value.slice(0, Math.round(value.length * progress));
  };

  return (
    <div className="relative min-h-[430px] overflow-hidden rounded-[24px] border border-[#e7dce7] bg-[#f8f9fa] p-5 shadow-[0_24px_70px_rgba(46,22,152,.08)]">
      <div
        className="absolute inset-0 flex items-center justify-center bg-[#f1f5f9] transition-opacity duration-300"
        style={{ opacity: desktopOpacity }}
      >
        <div className="relative flex w-[360px] max-w-full items-start justify-center gap-20">
          <div className="text-center">
            <motion.div
              className="mx-auto flex h-14 w-14 items-center justify-center rounded-[14px] border border-[#e7dce7] bg-white shadow-[0_18px_38px_rgba(82,35,177,.16)]"
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <div
                className="h-9 w-9"
                style={{
                  background: "linear-gradient(135deg, #5223b1 0%, #7823b1 50%, #e930c9 100%)",
                  WebkitMaskImage: `url(${logoSrc})`,
                  maskImage: `url(${logoSrc})`,
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                }}
              />
            </motion.div>
            <div className="mt-2 text-[11px] font-extrabold text-[#475569]">ClawEase</div>
          </div>

          <svg aria-hidden="true" className="absolute left-1/2 top-7 h-2 w-16 -translate-x-1/2">
            <line
              x1="0"
              y1="4"
              x2={64 * desktopLineProgress}
              y2="4"
              stroke="#5223b1"
              strokeDasharray="4 8"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>

          <div className="text-center">
            <motion.div
              className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-[0_14px_32px_rgba(15,23,42,.12)]"
              animate={{ scale: phase >= 1.2 && phase < 1.7 ? [1, 1.08, 1] : 1 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <span className="absolute inset-1.5 rounded-full bg-[conic-gradient(#ea4335_0_25%,#fbbc05_0_50%,#34a853_0_75%,#4285f4_0_100%)]" />
              <span className="absolute inset-[15px] rounded-full bg-[#4285f4]" />
              <span className="relative h-4 w-4 rounded-full bg-white" />
            </motion.div>
            <div className="mt-2 text-[11px] font-extrabold text-[#475569]">{t(lang, "howItWorks.visual.execute.chrome")}</div>
          </div>

          <motion.div
            className="absolute inset-x-0 top-[112px] mx-auto w-[280px] text-center"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: phase >= 0.9 && phase < 1.8 ? 1 : 0, y: phase >= 0.9 ? 0 : 6 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <div className="text-xs font-extrabold text-[#475569]">{t(lang, "howItWorks.visual.execute.control")}</div>
            <div className="mt-1 text-[11px] font-bold text-[#64748b]">{t(lang, "howItWorks.visual.execute.controlSub")}</div>
          </motion.div>
        </div>
      </div>

      <div
        className="absolute inset-5 origin-center overflow-hidden border border-[#e7dce7] bg-white shadow-[0_24px_70px_rgba(46,22,152,.08)]"
        style={{
          opacity: browserOpacity,
          transform: `scale(${browserScale})`,
          borderRadius: `${browserRadius}px`,
        }}
      >
        <div className="flex h-[54px] items-center gap-3 border-b border-[#f0edf2] bg-[#fcfbfd] px-5">
          <div className="flex gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff6b6b]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#f7c948]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#36b37e]" />
          </div>
          <div className="ml-3 flex min-w-0 flex-1 items-center justify-between rounded-full bg-white px-4 py-2 text-xs font-extrabold text-[#775f78] shadow-sm">
            <span className="min-w-0 truncate">
              {typedUrl}
              {phase >= 2.2 && phase < 2.92 && <span className="ml-0.5 text-[#5223b1]">|</span>}
            </span>
            <motion.span
              className="ml-2 text-[#775f78]"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: phase >= 2.9 ? 1 : 0, scale: phase >= 2.9 ? 1 : 0.8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              ☆
            </motion.span>
          </div>
        </div>

        <div className="relative h-[calc(100%-54px)] bg-white p-4">
          <motion.div
            className="absolute left-1/2 top-1/2 w-[66%] -translate-x-1/2 -translate-y-1/2 space-y-3"
            style={{ opacity: skeletonOpacity }}
          >
            <div className="h-4 w-1/2 rounded-full bg-[#e2e8f0]" />
            <div className="h-11 rounded-xl bg-gradient-to-r from-[#e2e8f0] via-[#f8fafc] to-[#e2e8f0]" />
            <div className="h-11 rounded-xl bg-gradient-to-r from-[#e2e8f0] via-[#f8fafc] to-[#e2e8f0]" />
            <div className="h-11 rounded-xl bg-gradient-to-r from-[#e2e8f0] via-[#f8fafc] to-[#e2e8f0]" />
          </motion.div>

          <motion.div
            className="mx-auto max-w-[500px] rounded-[20px] border border-[#e7dce7] bg-white p-3 shadow-[0_18px_44px_rgba(46,22,152,.08)]"
            style={{
              opacity: formOpacity,
              transform: `translateY(${(1 - formOpacity) * 10}px) scale(0.88)`,
              transformOrigin: "top center",
            }}
          >
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">{t(lang, "howItWorks.visual.execute.form")}</div>
                <div className="mt-0.5 text-base font-extrabold text-[#100f12]">{t(lang, "howItWorks.visual.execute.title")}</div>
              </div>
              <motion.div
                className="rounded-full bg-[#f3eefa] px-3 py-1 text-[11px] font-extrabold text-[#5223b1]"
                animate={{ opacity: [0.78, 1, 0.78] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                {t(lang, "howItWorks.visual.execute.auto")}
              </motion.div>
            </div>

            <div className="space-y-2">
              {fields.map((field) => {
                const value = fieldValueFor(field);
                const active = phase >= field.start - 0.12 && phase < field.end + 0.1;
                const complete = phase >= field.end;

                return (
                  <motion.div
                    key={field.label}
                    className="relative rounded-xl border bg-white px-3 py-2"
                    animate={{
                      borderColor: active ? "#5223b1" : complete ? "#a7f3d0" : "#e7dce7",
                      boxShadow: active ? "0 0 0 4px rgba(82,35,177,.08)" : "0 0 0 0 rgba(82,35,177,0)",
                    }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                  >
                    <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#775f78]">{t(lang, field.label)}</div>
                    <div className="mt-0.5 flex h-4 items-center justify-between gap-2 text-[13px] font-extrabold text-[#100f12]">
                      <span className={value ? "" : "h-2.5 w-28 rounded-full bg-[#f0edf2]"}>{value}</span>
                      {field.dropdown && <span className="text-xs text-[#775f78]">▾</span>}
                      {complete && <CheckCircle2 size={15} className="ml-auto shrink-0 text-[#059669]" />}
                    </div>
                    {field.dropdown && active && (
                      <motion.div
                        className="absolute left-3 right-3 top-[50px] z-20 rounded-xl border border-[#e7dce7] bg-white p-1.5 shadow-[0_14px_30px_rgba(46,22,152,.14)]"
                        initial={{ opacity: 0, scaleY: 0.86, transformOrigin: "top" }}
                        animate={{ opacity: 1, scaleY: 1 }}
                        transition={{ duration: 0.16, ease: "easeOut" }}
                      >
                        <div className="rounded-lg bg-[#f3eefa] px-2.5 py-1.5 text-xs font-extrabold text-[#5223b1]">{t(lang, field.value)}</div>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            <motion.button
              type="button"
              className="mt-3 inline-flex min-h-9 w-full items-center justify-center gap-2 rounded-xl bg-[#281629] text-sm font-extrabold text-white shadow-[0_12px_28px_rgba(40,22,41,.16)]"
              animate={{
                scale: submitPressed ? 0.96 : phase >= 5.76 && phase < 5.84 ? 1.03 : 1,
                opacity: submitting ? 0.78 : 1,
                boxShadow: submitPressed ? "0 0 22px rgba(82,35,177,.32)" : "0 12px 28px rgba(40,22,41,.16)",
              }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              {submitting && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              {submitting ? t(lang, "howItWorks.visual.execute.submitting") : t(lang, "howItWorks.visual.execute.submit")}
            </motion.button>
          </motion.div>

          {successVisible && (
            <>
              <div className="absolute inset-0 z-20 bg-white/60" />
              <motion.div
                className="absolute left-1/2 top-[42%] z-30 w-[260px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[#a7f3d0] bg-white p-4 text-center shadow-[0_20px_60px_rgba(5,150,105,.15)]"
                initial={false}
                animate={{
                  opacity: successProgress,
                  scale: 0.82 + successProgress * (successProgress < 0.75 ? 0.26 : 0.18),
                }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <CheckCircle2 size={28} className="mx-auto text-[#059669]" />
                <div className="mt-2 text-base font-extrabold text-[#059669]">{t(lang, "howItWorks.visual.execute.successTitle")}</div>
                <div className="mt-2 space-y-1 text-xs font-bold leading-relaxed text-[#3a383f]">
                  <div>{t(lang, "howItWorks.visual.execute.successLine1")}</div>
                  <div>{t(lang, "howItWorks.visual.execute.successLine2")}</div>
                </div>
                <motion.div
                  className="mx-auto mt-3 inline-flex rounded-full bg-[#f3eefa] px-3 py-1 text-[11px] font-extrabold text-[#5223b1]"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: phase >= 6.4 ? 1 : 0, y: phase >= 6.4 ? 0 : 4 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {t(lang, "howItWorks.visual.execute.completedBy")}
                </motion.div>
              </motion.div>
            </>
          )}
        </div>
      </div>

      {cursorPoint && (
        <motion.div
          className="pointer-events-none absolute z-50 text-[#5223b1] drop-shadow-[0_8px_14px_rgba(82,35,177,.26)]"
          style={{ left: `${cursorPoint.x}%`, top: `${cursorPoint.y}%` }}
          animate={{ scale: submitPressed || (phase >= 1.68 && phase < 1.82) ? 0.88 : 1 }}
          transition={{ duration: 0.12, ease: "easeOut" }}
        >
          <MousePointer2 size={22} fill="white" strokeWidth={2.4} />
        </motion.div>
      )}
    </div>
  );
}

function ReportVisual({ lang }: { lang: Lang }) {
  const navItems = [
    "howItWorks.visual.report.dashboard.nav.home",
    "howItWorks.visual.report.dashboard.nav.workflow",
    "howItWorks.visual.report.dashboard.nav.records",
    "howItWorks.visual.report.dashboard.nav.channels",
  ];
  const sidebarItems = [
    { icon: ChartNoAxesColumnIncreasing, text: "howItWorks.visual.report.sidebar.overview" },
    { icon: ListChecks, text: "howItWorks.visual.report.sidebar.workflow" },
    { icon: Activity, text: "howItWorks.visual.report.sidebar.records", active: true },
    { icon: Inbox, text: "howItWorks.visual.report.sidebar.channels" },
    { icon: Users, text: "howItWorks.visual.report.sidebar.team" },
    { icon: Settings, text: "howItWorks.visual.report.sidebar.settings" },
  ];
  const stats = [
    { value: "23", label: "howItWorks.visual.report.dashboard.stat.today" },
    { value: "100%", label: "howItWorks.visual.report.dashboard.stat.complete" },
    { value: "0", label: "howItWorks.visual.report.dashboard.stat.missed" },
    { value: lang === "zh" ? "1分24秒" : "1m24s", label: "howItWorks.visual.report.dashboard.stat.avgTime" },
  ];
  const records = [
    {
      time: "14:32",
      customer: "howItWorks.visual.report.record.customer1",
      business: "howItWorks.visual.report.record.business1",
      channel: "WhatsApp",
      status: "howItWorks.visual.report.record.statusSubmitted",
      duration: lang === "zh" ? "1m24s" : "1m24s",
      active: true,
    },
    {
      time: "14:18",
      customer: "howItWorks.visual.report.record.customer2",
      business: "howItWorks.visual.report.record.business2",
      channel: t(lang, "howItWorks.visual.report.record.phone"),
      status: "howItWorks.visual.report.record.statusConfirmed",
      duration: "0m48s",
    },
    {
      time: "13:55",
      customer: "howItWorks.visual.report.record.customer3",
      business: "howItWorks.visual.report.record.business3",
      channel: "LINE",
      status: "howItWorks.visual.report.record.statusReplied",
      duration: "0m32s",
    },
    {
      time: "13:42",
      customer: "howItWorks.visual.report.record.customer4",
      business: "howItWorks.visual.report.record.business4",
      channel: t(lang, "howItWorks.visual.report.record.form"),
      status: "howItWorks.visual.report.record.statusSubmitted",
      duration: "1m02s",
    },
  ];
  const dagNodes = [
    { title: "narrative", meta: "1ed7477...bdab7b", result: "2", x: 26, y: 34, active: true },
    { title: "event_group_analysis", meta: "b6d11f5...7bf341", result: "1", x: 196, y: 34 },
    { title: "navigation", meta: "cf17978...4837a9", result: "6", x: 196, y: 96 },
    { title: "event_explore", meta: "042303d...f35a0c", result: "2", x: 386, y: 34 },
    { title: "operation", meta: "e9e74f9...99df06", result: "3", x: 576, y: 34 },
    { title: "listen", meta: "c24ef00...31b2a7", result: "2", x: 576, y: 96 },
  ];
  const [activeReportCard, setActiveReportCard] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveReportCard((current) => (current + 1) % 3);
    }, 3400);

    return () => window.clearInterval(timer);
  }, []);

  const reportCardStates = {
    front: { x: 0, y: 0, scale: 1, opacity: 1, filter: "blur(0px)", zIndex: 30 },
    right: { x: 220, y: 42, scale: 0.72, opacity: 0.24, filter: "blur(6px)", zIndex: 10 },
    left: { x: -220, y: 42, scale: 0.72, opacity: 0.24, filter: "blur(6px)", zIndex: 10 },
  };
  const reportCardMotion = (index: number) => {
    const offset = (index - activeReportCard + 3) % 3;
    if (offset === 0) return reportCardStates.front;
    if (offset === 1) return reportCardStates.right;
    return reportCardStates.left;
  };
  const reportCardTransition = { duration: 0.62, ease: "easeInOut" as const };

  return (
    <motion.div
      className="relative min-h-[590px] overflow-hidden rounded-[24px] border border-[#e7dce7] bg-[#f8f9fa] shadow-[0_24px_70px_rgba(46,22,152,.08)] sm:min-h-[550px]"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.45 }}
      transition={{ duration: 0.42, ease: "easeOut" }}
    >
      <motion.div
        className="absolute left-1/2 top-[82px] h-[390px] w-[520px] origin-center overflow-hidden rounded-[28px] border border-[#e7dce7] bg-white shadow-[0_26px_70px_rgba(46,22,152,.10)]"
        style={{ marginLeft: -260 }}
        initial={false}
        animate={reportCardMotion(0)}
        transition={reportCardTransition}
      >
        <div className="flex h-12 items-center border-b border-[#f0edf2] px-4">
          <div className="flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center rounded-xl bg-white shadow-[0_10px_26px_rgba(82,35,177,.14)] ring-1 ring-[#efe7ef]">
              <img src={logoMarkSrc} alt="" className="h-5 w-5 object-contain" />
            </div>
            <span className="text-sm font-extrabold text-[#100f12]">ClawEase</span>
          </div>
          <div className="ml-6 hidden items-center gap-4 text-[11px] font-bold text-[#775f78] sm:flex">
            {navItems.map((item, index) => (
              <span key={item} className={index === 2 ? "border-b-2 border-[#5223b1] pb-3 text-[#100f12]" : ""}>
                {t(lang, item)}
              </span>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 text-[11px] font-bold text-[#775f78]">
            <span className="hidden sm:inline">{t(lang, "howItWorks.visual.report.dashboard.owner")}</span>
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#f3eefa] text-[#5223b1]">ZW</span>
          </div>
        </div>

        <div className="grid h-[calc(100%-48px)] grid-cols-[64px_1fr]">
          <div className="border-r border-[#f0edf2] bg-[#fbfafc] px-2 py-4">
            <div className="space-y-3">
              {sidebarItems.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.text}
                    className={[
                      "flex flex-col items-center gap-1 rounded-xl px-1.5 py-2 text-[9px] font-bold",
                      item.active ? "bg-[#f3eefa] text-[#5223b1]" : "text-[#94a3b8]",
                    ].join(" ")}
                  >
                    <Icon size={14} />
                    <span>{t(lang, item.text)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-3 sm:p-4">
            <div className="mb-2.5 flex items-center justify-between">
              <div>
                <div className="text-sm font-extrabold text-[#100f12]">
                  {t(lang, "howItWorks.visual.report.dashboard.title")}
                </div>
                <div className="mt-1 text-[10px] font-semibold text-[#94a3b8]">
                  {t(lang, "howItWorks.visual.report.dashboard.subtitle")}
                </div>
              </div>
              <div className="hidden rounded-full border border-[#e7dce7] bg-white px-2.5 py-1 text-[9px] font-extrabold text-[#775f78] sm:block">
                {t(lang, "howItWorks.visual.report.dashboard.today")}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {stats.map((item, index) => (
                <motion.div
                  key={item.label}
                  className="rounded-xl border border-[#e7dce7] bg-white p-2.5"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.45 }}
                  transition={{ duration: 0.28, ease: "easeOut", delay: 0.18 + index * 0.03 }}
                >
                  <div className="font-mono text-base font-extrabold tabular-nums text-[#100f12]">{item.value}</div>
                  <div className="mt-1 text-[9px] font-bold text-[#94a3b8]">{t(lang, item.label)}</div>
                </motion.div>
              ))}
            </div>

            <div className="mt-3 rounded-2xl border border-[#e7dce7] bg-white p-2.5">
              <div className="mb-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-extrabold text-[#100f12]">
                  <Activity size={14} className="text-[#5223b1]" />
                  {t(lang, "howItWorks.visual.report.record.title")}
                </div>
                <span className="rounded-full bg-[#f3eefa] px-2 py-1 text-[9px] font-extrabold text-[#5223b1]">
                  {t(lang, "howItWorks.visual.report.record.filter")}
                </span>
              </div>

              <div className="grid grid-cols-[38px_1fr_1fr_1fr_1.2fr_42px] border-b border-[#f0edf2] pb-1.5 text-[8px] font-extrabold uppercase tracking-[0.08em] text-[#94a3b8]">
                <span>{t(lang, "howItWorks.visual.report.record.time")}</span>
                <span>{t(lang, "howItWorks.visual.report.record.customer")}</span>
                <span>{t(lang, "howItWorks.visual.report.record.business")}</span>
                <span>{t(lang, "howItWorks.visual.report.record.channel")}</span>
                <span>{t(lang, "howItWorks.visual.report.record.status")}</span>
                <span>{t(lang, "howItWorks.visual.report.record.duration")}</span>
              </div>

              <div className="pt-1">
                {records.map((record, index) => (
                  <motion.div
                    key={`${record.time}-${record.customer}`}
                    className={[
                      "grid grid-cols-[38px_1fr_1fr_1fr_1.2fr_42px] items-center rounded-lg px-1.5 py-1.5 text-[9px] font-bold",
                      record.active ? "border-l-2 border-[#5223b1] bg-[#f5f0ff] text-[#100f12]" : "text-[#64748b]",
                    ].join(" ")}
                    initial={{ opacity: 0, y: -6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.45 }}
                    transition={{ duration: 0.24, ease: "easeOut", delay: 0.28 + index * 0.03 }}
                  >
                    <span className="font-mono tabular-nums">{record.time}</span>
                    <span>{t(lang, record.customer)}</span>
                    <span>{t(lang, record.business)}</span>
                    <span>{record.channel}</span>
                    <span className="inline-flex items-center gap-1 text-[#059669]">
                      <CheckCircle2 size={10} />
                      {t(lang, record.status)}
                    </span>
                    <span className="font-mono tabular-nums">{record.duration}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="mt-2 h-[170px] overflow-hidden rounded-2xl border border-[#e7dce7] bg-[#f8fafc] p-2.5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5 text-xs font-extrabold text-[#100f12]">
                    <Activity size={13} className="text-[#5223b1]" />
                    {t(lang, "howItWorks.visual.report.dag.title")}
                  </div>
                  <div className="mt-0.5 text-[9px] font-semibold text-[#94a3b8]">
                    {t(lang, "howItWorks.visual.report.dag.subtitle")}
                  </div>
                </div>
                <span className="rounded-full border border-[#e7dce7] bg-white px-2 py-0.5 text-[8px] font-extrabold text-[#475569]">
                  {t(lang, "howItWorks.visual.report.dag.count")}
                </span>
              </div>

              <div className="relative mt-2 h-[122px] min-w-[740px]">
                <svg aria-hidden="true" className="absolute inset-0 h-full w-full">
                  <defs>
                    <marker id="reportDagArrow" markerHeight="6" markerWidth="6" orient="auto" refX="5" refY="3">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#cbd5e1" />
                    </marker>
                    <marker id="reportDagArrowActive" markerHeight="6" markerWidth="6" orient="auto" refX="5" refY="3">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#7c6dff" />
                    </marker>
                  </defs>
                  <path d="M145 60 C168 60 170 60 190 60" stroke="#7c6dff" strokeWidth="2" markerEnd="url(#reportDagArrowActive)" fill="none" />
                  <path d="M145 60 C168 60 165 122 190 122" stroke="#7c6dff" strokeWidth="2" markerEnd="url(#reportDagArrowActive)" fill="none" opacity="0.82" />
                  <path d="M315 60 C350 60 350 60 380 60" stroke="#cbd5e1" strokeWidth="1.5" markerEnd="url(#reportDagArrow)" fill="none" />
                  <path d="M315 60 C350 60 350 122 380 122" stroke="#cbd5e1" strokeWidth="1.5" markerEnd="url(#reportDagArrow)" fill="none" />
                  <path d="M505 60 C540 60 540 60 570 60" stroke="#cbd5e1" strokeWidth="1.5" markerEnd="url(#reportDagArrow)" fill="none" />
                  <path d="M505 122 C540 122 540 122 570 122" stroke="#cbd5e1" strokeWidth="1.5" markerEnd="url(#reportDagArrow)" fill="none" />
                </svg>

                {dagNodes.map((node, index) => (
                  <motion.div
                    key={`${node.title}-${node.meta}`}
                    className={[
                      "absolute w-[122px] rounded-xl border bg-white p-2 shadow-[0_8px_18px_rgba(15,23,42,.06)]",
                      node.active ? "border-[#7c6dff] ring-2 ring-[#7c6dff]/10" : "border-[#dbe3ef]",
                    ].join(" ")}
                    style={{ left: node.x, top: node.y }}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.45 }}
                    transition={{ duration: 0.22, ease: "easeOut", delay: 0.48 + index * 0.04 }}
                  >
                    <div className="truncate text-[9px] font-extrabold text-[#1f2937]">{node.title}</div>
                    <div className="mt-1 truncate font-mono text-[8px] font-bold text-[#64748b]">{node.meta}</div>
                    <div className="mt-1 flex items-center justify-between text-[8px] font-bold text-[#64748b]">
                      <span>05/15 11:{index + 8}</span>
                      <span>{node.result} {t(lang, "howItWorks.visual.report.dag.result")}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute left-1/2 top-[78px] w-[184px] origin-center"
        style={{ marginLeft: -92 }}
        initial={false}
        animate={reportCardMotion(1)}
        transition={reportCardTransition}
      >
        <div className="relative rounded-[34px] bg-[#15151a] p-[7px] shadow-[0_30px_60px_rgba(0,0,0,.18)]">
          <span className="absolute -right-1 top-20 h-12 w-1.5 rounded-r-full bg-[#15151a]" />
          <span className="absolute -left-1 top-16 h-8 w-1.5 rounded-l-full bg-[#15151a]" />
          <div className="relative h-[326px] overflow-hidden rounded-[27px] bg-[linear-gradient(180deg,#f8f4ff_0%,#f7f9fc_58%,#eef2f7_100%)] sm:h-[342px]">
            <div className="absolute left-1/2 top-2 z-20 h-[18px] w-[72px] -translate-x-1/2 rounded-full bg-[#101012] shadow-inner" />
            <div className="flex h-8 items-center justify-between px-3.5 pt-2 text-[8px] font-extrabold text-[#100f12]">
              <span>14:32</span>
              <span className="font-mono">5G 78%</span>
            </div>

            <div className="px-3 pt-5 text-center">
              <div className="text-[23px] font-extrabold tracking-[-0.04em] text-[#100f12]">14:32</div>
              <div className="mt-1 text-[8px] font-bold text-[#775f78]">{t(lang, "howItWorks.visual.report.phone.owner")}</div>
            </div>

            <motion.div
              className="mx-3.5 mt-4 rounded-[17px] border border-white/80 bg-white/90 p-2.5 shadow-[0_14px_28px_rgba(46,22,152,.12)] backdrop-blur"
              initial={{ opacity: 0, y: 12, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.45 }}
              transition={{ duration: 0.32, ease: "easeOut", delay: 0.9 }}
            >
              <div className="flex items-center justify-between text-[8px] font-extrabold text-[#775f78]">
                <span>{t(lang, "howItWorks.visual.report.phone.app")}</span>
                <span>{t(lang, "howItWorks.visual.report.phone.now")}</span>
              </div>
              <div className="mt-2 flex gap-1.5">
                <div className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-[#f3eefa] text-[#5223b1]">
                  <MessageSquare size={12} />
                </div>
                <div className="min-w-0 text-left">
                  <div className="text-[9px] font-extrabold text-[#100f12]">{t(lang, "howItWorks.visual.report.phone.bot")}</div>
                  <div className="mt-1 space-y-0.5 text-[8.5px] font-bold leading-snug text-[#3a383f]">
                    <div>{t(lang, "howItWorks.visual.report.phone.title")}</div>
                    <div>{t(lang, "howItWorks.visual.report.phone.subtitle")}</div>
                    <div className="inline-flex items-center gap-1 rounded-full bg-[#ecfdf5] px-1.5 py-0.5 text-[8px] text-[#059669]">
                      <CheckCircle2 size={9} />
                      {t(lang, "howItWorks.visual.report.phone.status")}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="absolute bottom-3 left-1/2 h-1 w-20 -translate-x-1/2 rounded-full bg-[#100f12]/22" />
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute left-1/2 top-[96px] w-[360px] max-w-[76%] origin-center rounded-[24px] border border-[#e7dce7] bg-white shadow-[0_34px_80px_rgba(46,22,152,.18)] sm:w-[400px]"
        style={{ marginLeft: -200 }}
        initial={false}
        animate={reportCardMotion(2)}
        transition={reportCardTransition}
      >
        <div className="flex items-center justify-between border-b border-[#f0edf2] px-3.5 py-2.5">
          <div className="flex items-center gap-2 text-[11px] font-extrabold text-[#100f12]">
            <MessageSquare size={14} className="text-[#5223b1]" />
            {t(lang, "howItWorks.visual.report.chat.group")}
          </div>
          <div className="hidden gap-1 text-[8px] font-extrabold text-[#94a3b8] sm:flex">
            <span className="rounded-full bg-[#f3eefa] px-1.5 py-0.5 text-[#5223b1]">{t(lang, "howItWorks.visual.report.chat.tab1")}</span>
            <span className="rounded-full px-1.5 py-0.5">{t(lang, "howItWorks.visual.report.chat.tab2")}</span>
            <span className="rounded-full px-1.5 py-0.5">{t(lang, "howItWorks.visual.report.chat.tab3")}</span>
          </div>
        </div>

        <div className="space-y-1.5 p-2.5 pb-2.5">
          <motion.div
            className="flex max-w-[78%] items-start gap-1.5"
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.26, ease: "easeOut", delay: 1.18 }}
          >
            <span className="grid h-[20px] w-[20px] shrink-0 place-items-center rounded-full bg-[#f3eefa] text-[#5223b1]">
              <Bot size={11} />
            </span>
            <div className="min-w-0 rounded-2xl rounded-tl-md bg-[#f5f0ff] px-2.5 py-2">
              <div className="mb-1 flex items-center gap-1.5 text-[9px] font-extrabold text-[#775f78]">
                ClawEase Bot
                <span className="font-mono text-[8px] text-[#b5a7b8]">14:32</span>
              </div>
              <div className="space-y-0.5 text-[10px] font-bold leading-snug text-[#3a383f]">
                <div>{t(lang, "howItWorks.visual.report.chat.botTitle")}</div>
                <div>{t(lang, "howItWorks.visual.report.chat.botLine1")}</div>
                <div>{t(lang, "howItWorks.visual.report.chat.botLine2")}</div>
                <div className="inline-flex items-center gap-1 text-[#059669]">
                  <CheckCircle2 size={10} />
                  {t(lang, "howItWorks.visual.report.chat.botLine3")}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex justify-end"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.26, ease: "easeOut", delay: 1.34 }}
          >
            <div className="max-w-[64%] rounded-2xl rounded-tr-md bg-[#281629] px-2.5 py-2 text-white shadow-[0_10px_24px_rgba(40,22,41,.14)]">
              <div className="mb-1 flex items-center justify-end gap-1.5 text-[8px] font-extrabold text-white/60">
                <span className="font-mono">14:33</span>
                {t(lang, "howItWorks.visual.report.chat.manager")}
              </div>
              <div className="text-[10px] font-bold leading-snug">{t(lang, "howItWorks.visual.report.chat.managerReply")}</div>
            </div>
          </motion.div>

          <motion.div
            className="flex max-w-[70%] items-start gap-1.5"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.26, ease: "easeOut", delay: 1.5 }}
          >
            <span className="grid h-[20px] w-[20px] shrink-0 place-items-center rounded-full bg-[#ecfdf5] text-[9px] font-extrabold text-[#059669]">L</span>
            <div className="min-w-0 rounded-2xl rounded-tl-md bg-[#f8f9fa] px-2.5 py-2">
              <div className="mb-1 flex items-center gap-1.5 text-[8px] font-extrabold text-[#775f78]">
                {t(lang, "howItWorks.visual.report.chat.staff")}
                <span className="font-mono text-[#b5a7b8]">14:33</span>
              </div>
              <div className="text-[10px] font-bold leading-snug text-[#3a383f]">{t(lang, "howItWorks.visual.report.chat.staffReply")}</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="absolute bottom-7 left-1/2 z-40 flex -translate-x-1/2 items-center gap-1.5">
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className={[
              "h-2.5 rounded-full transition-all duration-300",
              activeReportCard === index ? "w-7 bg-[#100f12]" : "w-2.5 bg-[#d6d2d8]",
            ].join(" ")}
          />
        ))}
      </div>
    </motion.div>
  );
}

function StepVisual({ type, lang }: { type: VisualType; lang: Lang }) {
  if (type === "learn") return <LearnVisual lang={lang} />;
  if (type === "catch") return <CatchVisual lang={lang} />;
  if (type === "understand") return <UnderstandVisual lang={lang} />;
  if (type === "execute") return <ExecuteVisual lang={lang} />;
  return <ReportVisual lang={lang} />;
}

export function HowItWorks({ lang }: { lang: Lang }) {
  return (
    <section id="how" className="relative overflow-hidden bg-[#f8f9fa] px-5 pb-24 pt-14 sm:px-10 lg:pb-28 lg:pt-16">
      <div className="relative mx-auto max-w-[1200px]">
        <div className="mx-auto max-w-[980px] text-center">
          <Badge>{t(lang, "howItWorks.badge")}</Badge>
          <h2 className="mt-5 text-[34px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[#100f12] sm:text-[48px] md:text-[58px]">
            {t(lang, "howItWorks.title")}
          </h2>
          <p className="mx-auto mt-6 max-w-[860px] text-base leading-relaxed text-[#3a383f] sm:text-lg">
            {t(lang, "howItWorks.sub")}
          </p>
        </div>

        <div className="relative mt-20">
          <div className="space-y-14">
            {steps.map((step) => (
              <motion.article
                key={step.key}
                className={[
                  "grid items-center gap-8 py-6 lg:gap-20 lg:py-8",
                  step.reverse ? "lg:grid-cols-[3fr_2fr]" : "lg:grid-cols-[2fr_3fr]",
                ].join(" ")}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.42, once: false }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <div className={step.reverse ? "lg:order-2" : "lg:order-1"}>
                  <div
                    className={[
                      "flex flex-col",
                      step.reverse ? "items-start text-left" : "items-start text-left lg:items-end lg:text-right",
                    ].join(" ")}
                  >
                    <Badge>{t(lang, `${step.key}.label`)}</Badge>
                    <h3 className="mt-4 text-3xl font-extrabold tracking-[-0.02em] text-[#100f12] sm:text-4xl">
                      {t(lang, `${step.key}.title`)}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-[#3a383f]">
                      {t(lang, `${step.key}.desc`)}
                    </p>
                    <div className={["mt-6 flex flex-wrap gap-2.5", step.reverse ? "justify-start" : "justify-start lg:justify-end"].join(" ")}>
                      {step.tags.map((tag) => (
                        <FeatureTag key={tag}>{t(lang, tag)}</FeatureTag>
                      ))}
                    </div>
                  </div>
                </div>
                <motion.div
                  className={step.reverse ? "lg:order-1" : "lg:order-2"}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ amount: 0.5, once: false }}
                  transition={{ duration: 0.68, ease: "easeOut", delay: 0.08 }}
                >
                  <StepVisual type={step.visual} lang={lang} />
                </motion.div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
